import React, { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { addDoc, collection, query, orderBy, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '../../../firebase';
import ApplicationTable from './components/ApplicationTable';
import ApplicationFilter from './components/ApplicationFilter';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import SmsService from '../../../services/SmsService';
import {
  getAdminScrollContainer,
  getAdminScrollMetrics,
  getAdminScrollTop,
  setAdminScrollTop,
} from '../../../utils/adminScroll';
import { PORTVILLE_APPLICATIONS_LAST_SCROLL_KEY } from '../../../utils/portvilleApplicationsScroll';
import {
  matchesPortvilleApplicationFilter,
  type PortvilleApplicationFilterKey,
} from '../../../utils/portvilleApplicationStatus';

interface PortvilleApplication {
  id: string;
  uid: string;
  name: string;
  email: string;
  phoneNumber: string;
  organization: string;
  userLevel: number;
  additionalRequest: string;
  applyCode?: string;
  approvedAt?: any;
  documents: {
    identification: string;
    employment: string;
    other?: string;
  };
  status: 'pending' | 'checked' | 'approved' | 'rejected' | 'consumed';
  consumedReason?: string;
  createdAt: any;
  updatedAt: any;
}

interface PortvilleApplicationSmsLog {
  type: 'approved_notice' | 'rejected_notice';
  isResend: boolean;
  phoneNumber: string;
  sentAt: any;
  success: boolean;
  reason?: string;
}

const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const formatYyyyMmDdDots = (date: Date): string => {
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd}`;
};

const PortvilleManagePage: React.FC = () => {
  const [applications, setApplications] = useState<PortvilleApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<PortvilleApplicationFilterKey>('all');
  const [sendingTempCodes, setSendingTempCodes] = useState(false);
  const [isStreamConnected, setIsStreamConnected] = useState(false);
  const { user, userLevel, permissions, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [pendingRestorePos, setPendingRestorePos] = useState<number | null>(null);

  // Firebase 실시간 스트림을 위한 구독 설정
  const setupApplicationsStream = useCallback(() => {
    try {
      setLoading(true);
      const applicationsRef = collection(db, 'portville_applications');
      const q = query(applicationsRef, orderBy('createdAt', 'desc'));
      
      // 실시간 리스너 설정
      const unsubscribe = onSnapshot(q, 
        (querySnapshot) => {
          const applicationsData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as PortvilleApplication[];

          setApplications(applicationsData);
          setError(null);
          setLoading(false);
          setIsStreamConnected(true);
        },
        (err) => {
          console.error('신청서 목록 실시간 업데이트 오류:', err);
          setError('신청서 목록을 불러오는 중 오류가 발생했습니다.');
          setLoading(false);
          setIsStreamConnected(false);
        }
      );

      return unsubscribe;
    } catch (err) {
      console.error('신청서 목록 스트림 설정 오류:', err);
      setError('신청서 목록을 불러오는 중 오류가 발생했습니다.');
      setLoading(false);
      return () => {}; // 빈 cleanup 함수 반환
    }
  }, []);

  useEffect(() => {
    if (!authLoading) {
      // 최고관리자이거나 포트빌 관리 권한이 있는 경우에만 접근 가능
      if (!user || (userLevel !== 1 && !permissions?.portville)) {
        navigate('/admin/dashboard');
        return;
      }

      // 실시간 스트림 설정
      const unsubscribe = setupApplicationsStream();

      // cleanup 함수 - 컴포넌트 언마운트 시 구독 해제
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [user, userLevel, permissions, authLoading, navigate, setupApplicationsStream]);

  useEffect(() => {
    const container = getAdminScrollContainer();
    if (!container) return;

    let rafId = 0;
    const saveScrollPosition = () => {
      sessionStorage.setItem(PORTVILLE_APPLICATIONS_LAST_SCROLL_KEY, String(container.scrollTop));
    };
    const handleScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        saveScrollPosition();
      });
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const restoreRequested = Boolean((location.state as { restoreScroll?: boolean } | null)?.restoreScroll);
    if (!restoreRequested && navigationType !== 'POP') return;

    const savedPosition = sessionStorage.getItem(PORTVILLE_APPLICATIONS_LAST_SCROLL_KEY);
    if (savedPosition == null) return;

    const position = Number.parseInt(savedPosition, 10);
    if (!Number.isNaN(position)) setPendingRestorePos(position);
  }, [location.state, navigationType]);

  useLayoutEffect(() => {
    if (pendingRestorePos == null || loading) return;

    const target = pendingRestorePos;
    const startedAt = performance.now();
    let rafId = 0;

    const restoreWhenReady = () => {
      const { scrollHeight, viewportHeight } = getAdminScrollMetrics();
      const maxScroll = Math.max(0, scrollHeight - viewportHeight);

      if (maxScroll >= target || performance.now() - startedAt > 800) {
        setAdminScrollTop(Math.min(target, maxScroll));
        setPendingRestorePos(null);
        return;
      }

      rafId = window.requestAnimationFrame(restoreWhenReady);
    };

    rafId = window.requestAnimationFrame(restoreWhenReady);
    return () => window.cancelAnimationFrame(rafId);
  }, [applications.length, loading, pendingRestorePos]);

  const handleOpenApplication = useCallback((applicationId: string) => {
    sessionStorage.setItem(PORTVILLE_APPLICATIONS_LAST_SCROLL_KEY, String(getAdminScrollTop()));
    navigate(`/admin/portville/applications/${applicationId}`);
  }, [navigate]);

  // 연결 재설정 함수 (실시간 스트림 재연결용)
  const handleReconnect = useCallback(() => {
    setLoading(true);
    setError(null);
    
    // 잠시 후 스트림 재설정 (기존 스트림은 useEffect cleanup에서 자동 해제됨)
    setTimeout(() => {
      window.location.reload(); // 페이지 새로고침으로 스트림 완전 재설정
    }, 100);
  }, []);

  const writeApprovedSmsLog = useCallback(async (applicationId: string, log: PortvilleApplicationSmsLog) => {
    const logsRef = collection(db, 'portville_applications', applicationId, 'sms_logs');
    await addDoc(logsRef, log);
  }, []);

  // SMS 발송 함수
  const handleSendSMS = useCallback(async (application: PortvilleApplication, newStatus: string, cancelReason?: string) => {
    if (!application.phoneNumber) return;

    try {
      let smsMessage = '';
      
	      if (newStatus === 'checked') {
	        smsMessage = `[포트빌] ${application.name} 님 입주 대기 신청이 완료되었습니다. 홈페이지에서 대기 순번을 확인하세요.`;
	      } else if (newStatus === 'approved') {
	        const approvedAtDate = toDate(application.approvedAt) ?? new Date();
	        const deadlineText = formatYyyyMmDdDots(addDays(approvedAtDate, 7));
	        smsMessage = `[포트빌] ${application.name} 님. 입주 순번이 도래했습니다. ${deadlineText}까지 부산항신항시설관리센터 홈페이지에 로그인 후 확인해 주세요. 기한 초과시 대기가 취소되어 다음 순번으로 넘어가게 됩니다.`;

	      } else if (newStatus === 'rejected') {
	        smsMessage = `[포트빌] ${application.name} 님 입주 신청이 취소되었습니다.`;
	      }

	      if (smsMessage) {
	        await SmsService.sendSMS(
	          application.phoneNumber,
	          smsMessage,
	          newStatus === 'approved' ? { subject: '포트빌 입주 안내' } : undefined
	        );	        console.log('포트빌 상태변경 SMS 발송 완료:', application.phoneNumber);

	        if (newStatus === 'approved' && application.id) {
	          try {
	            await writeApprovedSmsLog(application.id, {
	              type: 'approved_notice',
	              isResend: false,
	              phoneNumber: application.phoneNumber,
	              sentAt: new Date(),
	              success: true,
	            });
	          } catch (logError) {
	            console.error('입주 안내 문자 로그 저장 실패:', logError);
	          }
	        }

	        if (newStatus === 'rejected' && application.id) {
	          if (cancelReason?.trim()) {
	            await SmsService.sendSMS(application.phoneNumber, `취소 사유: ${cancelReason.trim()}`);
	          }

	          try {
	            await writeApprovedSmsLog(application.id, {
	              type: 'rejected_notice',
	              isResend: false,
	              phoneNumber: application.phoneNumber,
	              sentAt: new Date(),
	              success: true,
	              reason: cancelReason?.trim(),
	            });
	          } catch (logError) {
	            console.error('취소 안내 문자 로그 저장 실패:', logError);
	          }
	        }
	      }
    } catch (smsError) {
      console.error('SMS 발송 실패:', smsError);

      if (newStatus === 'approved' && application.id) {
        try {
          await writeApprovedSmsLog(application.id, {
            type: 'approved_notice',
            isResend: false,
            phoneNumber: application.phoneNumber,
            sentAt: new Date(),
            success: false,
          });
        } catch (logError) {
          console.error('입주 안내 문자 실패 로그 저장 실패:', logError);
        }
      }

      if (newStatus === 'rejected' && application.id) {
        try {
          await writeApprovedSmsLog(application.id, {
            type: 'rejected_notice',
            isResend: false,
            phoneNumber: application.phoneNumber,
            sentAt: new Date(),
            success: false,
            reason: cancelReason?.trim(),
          });
        } catch (logError) {
          console.error('취소 안내 문자 실패 로그 저장 실패:', logError);
        }
      }

      // SMS 발송 실패는 상태 업데이트를 롤백하지 않음
      alert('상태는 변경되었으나 SMS 발송에 실패했습니다.');
	         }
	   }, [writeApprovedSmsLog]);

  // 임시코드 발송 함수
  const handleSendTempCodes = useCallback(async () => {
    try {
      setSendingTempCodes(true);

      // 조건에 맞는 사용자 필터링
      const targetApplications = applications.filter(app => {
        return (
          (!app.uid || app.uid.trim() === '') && // uid가 공백
          app.name && app.name.trim() !== '' && // name이 존재
          app.phoneNumber && app.phoneNumber.trim() !== '' && // phoneNumber가 존재
          app.applyCode && app.applyCode.trim() !== '' && // applyCode가 존재
          app.status === 'pending' // status가 pending
        );
      });

      if (targetApplications.length === 0) {
        alert('임시코드 발송 조건에 맞는 신청자가 없습니다.');
        return;
      }

      // 최종 확인
      const confirmed = window.confirm(
        `임시코드 발송 대상자: ${targetApplications.length}명\n정말 발송하시겠습니까?`
      );

      if (!confirmed) {
        return;
      }

      // SMS 발송
      let successCount = 0;
      let failCount = 0;

      for (const application of targetApplications) {
        try {
          const smsMessage = `[포트빌] ${application.name} 님. 입주 신청 전환용 코드는 ${application.applyCode} 입니다.`;
          await SmsService.sendSMS(application.phoneNumber, smsMessage);
          console.log('임시코드 SMS 발송 완료:', application.phoneNumber);
          successCount++;
        } catch (smsError) {
          console.error('임시코드 SMS 발송 실패:', application.phoneNumber, smsError);
          failCount++;
        }
      }

      // 결과 알림
      if (failCount === 0) {
        alert(`임시코드가 성공적으로 발송되었습니다. (${successCount}명)`);
      } else {
        alert(`임시코드 발송 완료.\n성공: ${successCount}명, 실패: ${failCount}명`);
      }

    } catch (error) {
      console.error('임시코드 발송 중 오류:', error);
      alert('임시코드 발송 중 오류가 발생했습니다.');
    } finally {
      setSendingTempCodes(false);
    }
  }, [applications]);

  const filteredApplications =
    filterStatus === 'all'
      ? applications
      : applications.filter(app => matchesPortvilleApplicationFilter(app, filterStatus));

  if (loading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1600px] mx-auto relative pt-8 px-6">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              포트빌 입주 신청 관리
            </div>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 안내 메시지 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-6 bg-[#f2f6fb] border-l-4 border-[#155acb] rounded-r-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
                  포트빌 입주 신청 목록을 관리하고 승인/취소할 수 있습니다.
                </p>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${isStreamConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className={`text-sm font-medium ${isStreamConnected ? 'text-green-600' : 'text-red-600'}`}>
                    {isStreamConnected ? '실시간 연결됨' : '연결 끊김'}
                  </span>
                </div>
              </div>
              <button
                onClick={handleSendTempCodes}
                disabled={sendingTempCodes}
                className="px-4 py-2 bg-[#155acb] text-white text-sm font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sendingTempCodes ? '발송 중...' : '임시코드 발송'}
              </button>
            </div>
          </div>
        </div>

        {/* 필터 섹션 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              신청 상태 필터
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            
            <ApplicationFilter
              currentFilter={filterStatus}
              onFilterChange={setFilterStatus}
            />
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-6 bg-red-50 border-l-4 border-red-500 rounded-r-sm">
              <p className="text-red-700 text-lg font-medium font-['Pretendard']">
                {error}
              </p>
            </div>
          </div>
        )}

        {/* 신청 목록 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              신청 목록
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
            
            <ApplicationTable
              applications={filteredApplications}
              loading={loading}
              onRefresh={handleReconnect}
              onSendSMS={handleSendSMS}
              onApplicationOpen={handleOpenApplication}
            />
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default PortvilleManagePage; 
